import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { addressSchema } from "./customer";
import { ReturnProductSchema } from "./draft-return/draft-return-items";
import { ExtendedWarrantySchema } from "./extended-warranty";
import { PurchaseChannel } from "./warranties";

const SharedSourceFields = { channelName: z.string().optional() };

const OnlineSourceSchema = z.object({
  purchaseChannel: z.literal(PurchaseChannel.ONLINE),
  orderId: zExt.objectId(),
  referenceId: z.string(),
  ...SharedSourceFields,
});

const RetailSourceSchema = z.object({
  purchaseChannel: z.literal(PurchaseChannel.RETAIL),
  ...SharedSourceFields,
});

export const WarrantyRegistrationSourceSchema = z.discriminatedUnion(
  "purchaseChannel",
  [OnlineSourceSchema, RetailSourceSchema],
);

export type WarrantyRegistrationSource = z.infer<
  typeof WarrantyRegistrationSourceSchema
>;

export const WarrantyRegistrationResponseType = [
  "images",
  "text_area",
  "videos",
  "multiple_choice",
] as const;

export type WarrantyRegistrationResponseType =
  (typeof WarrantyRegistrationResponseType)[number];

export const WarrantyRegistrationSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  email: z.string(),
  name: z.string(),
  product: ReturnProductSchema,
  // Dual-written as `[product]` for every registration. Source of truth going
  // forward; optional until existing documents are backfilled.
  products: z.array(ReturnProductSchema).optional(),
  responses: z.array(
    z.object({
      type: createEnum(["images", "text_area", "videos", "multiple_choice"]),
      value: z.any(),
      message: z.string(),
    }),
  ),
  customer: zExt.objectId().nullish(),
  customerData: z
    .object({
      _id: zExt.objectId(),
      firstName: z.string(),
      lastName: z.string(),
      address: addressSchema.nullish(),
    })
    .nullish(),
  returnId: zExt.objectId().nullish(),
  extendedWarranty: ExtendedWarrantySchema.nullish(),
  optInMarketing: z.boolean().nullish(),
  discountCode: z.string().nullish(),
  source: WarrantyRegistrationSourceSchema.nullish(),
  createdAt: z.date(),
  // PaymentIntent is excluded since it contains sensitive data
  requiresProductValidation: z.boolean().nullish(),
  customerProvidedProductName: z.string().nullish(),
  customerProvidedProductPhotos: z.array(z.string()).nullish(),
  customerProvidedProductPrice: z.string().nullish(),
});

export type WarrantyRegistration = z.infer<typeof WarrantyRegistrationSchema>;

export const REDO_WARRANTY_REGISTRATION_ID_ATTRIBUTE_KEY =
  "_redo_warranty_registration_id";

export function getCustomerName(registration: WarrantyRegistration): string {
  const { firstName, lastName } = registration.customerData ?? {};
  if (!firstName) {
    return "Unknown";
  }
  return lastName ? `${firstName} ${lastName}` : firstName;
}
